import React from 'react';
import { WorkspaceProps } from 'common/types/search';
import { workspaceStore } from '../../../../../stores/WorkspaceStore';
import { ReactComponent as Chevron } from '../../../../../assets/icons/chevron.svg';
import { ReactComponent as WorkspaceIcon } from '../../../../../assets/icons/redesign_workspace_icon.svg';
import styles from './WorkspaceFilter.module.scss';
import { useResultListContext } from 'common/contexts/ResultListContext';
import {addGTMEvent} from "../../../../../helpers/analytics";

const switches = {
  numSpaces: 'Any Workspace',
  deskCount: 'Coworking Space',
  privateOfficeCount: 'Private Office',
  virtualOfficeCount: 'Virtual Office',
  loungeCount: 'Airport Lounge',
};

const requestSwitches = {
  privateOfficeCount: 'private_offices',
  deskCount: 'desks',
  virtualOfficeCount: 'virtual_offices',
  loungeCount: 'lounge',
  numSpaces: 'any',
};

const WORKSPACES_ORDER = [
  'deskCount', 'privateOfficeCount', 'virtualOfficeCount', 'loungeCount'
];

const WorkspaceFilter: React.FC<WorkspaceProps> = ({ onWorkspaceHandler }) => {
  const { anyWorkspaceChecked, setAnyWorkspaceChecked } = workspaceStore;

  const { workspaceType, setWorkspaceType, spacesCount } = useResultListContext();

  const [isFilterVisible, setIsFilterVisible] = React.useState(false);
  const [checkedCount, setCheckedCount] = React.useState(0);

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevIsFilterVisible) => !prevIsFilterVisible);
    if(isFilterVisible){
      addGTMEvent('workspace_filter_menu_close', {  'listings_view_type' : viewParam });
    } else {
      addGTMEvent('workspace_filter_menu_open', {  'listings_view_type' : viewParam });
    }
  };
  const viewParam = new URLSearchParams(window.location.search).get('view');

  const handleBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsFilterVisible(false);
    addGTMEvent('workspace_filter_menu_close', {  'listings_view_type' : viewParam });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.name;
    const isChecked = event.target.checked;
    addGTMEvent('workspace_type_filter_menu_apply', {  'listings_view_type' : viewParam, workspace_type_selection: key });
    if (key === 'numSpaces') {
      setWorkspaceType(() => ({
        deskCount: isChecked,
        privateOfficeCount: isChecked,
        // meetingRoomCount: isChecked,
        virtualOfficeCount: isChecked,
        loungeCount: isChecked,
      }));
      setAnyWorkspaceChecked(isChecked);
      onWorkspaceHandler([
        requestSwitches.deskCount,
        requestSwitches.privateOfficeCount,
        // requestSwitches.meetingRoomCount,
        requestSwitches.virtualOfficeCount,
        requestSwitches.loungeCount,
      ]);
    } else {
      setWorkspaceType((prevState) => ({ ...prevState, [key]: isChecked }));
      setAnyWorkspaceChecked(false);

      const checkedValues = Object.entries({ ...workspaceType, [key]: isChecked })
        .filter(([, value]) => value)
        .map(([el]) => requestSwitches[el as keyof typeof requestSwitches]);

      onWorkspaceHandler(checkedValues);
    }
  };

  React.useEffect(() => {
    const count = Object.values(workspaceType).filter(Boolean).length;
    setCheckedCount(count);
  }, [workspaceType]);

  React.useEffect(() => {
    const anyWorkspaceCount = +spacesCount.numSpaces;
    Object.entries(spacesCount).forEach(([key, currentWorkspaceCount]) => {
      if (typeof currentWorkspaceCount === 'number' && currentWorkspaceCount > anyWorkspaceCount) {
        spacesCount[key] = anyWorkspaceCount;
      }
    });
  }, [spacesCount])

  return (
    <button className={isFilterVisible || checkedCount > 0 ? styles.container_active : styles.container} onClick={toggleFilterVisibility} type="button">
      <WorkspaceIcon />
      Workspace
      <span style={{ display: checkedCount ? 'block' : 'none' }}>
        {`(${checkedCount})`}
      </span>
      <Chevron style={{ transform: isFilterVisible ? 'rotate(180deg)' : 'none' }} />
      {isFilterVisible && (
        <div className={styles.background} onClick={handleBackgroundClick}>
          <div
            className={styles.container__filters}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <label key="numSpaces" className={styles.container__filters__option}>
              <div>
                <input
                  checked={anyWorkspaceChecked || false}
                  id="numSpaces"
                  name="numSpaces"
                  onChange={handleCheckboxChange}
                  type="checkbox"
                />
                <span className={styles.container__filters__option__checkbox} />
              </div>
              <span className={styles.container__filters__option__checkbox_text}>
                {`${switches.numSpaces} (${spacesCount.numSpaces})`}
              </span>
            </label>
            {WORKSPACES_ORDER.map((key) => {
              const value = spacesCount[key];

              if (!value || key === 'numSpaces' || key === 'prices' || key === 'numSpacesFiltered') {
                return null;
              }

              return (
                <label key={key} className={styles.container__filters__option}>
                  <div>
                    <input
                      checked={workspaceType[key] || false}
                      id={key}
                      name={key}
                      onChange={handleCheckboxChange}
                      type="checkbox"
                    />
                    <span className={styles.container__filters__option__checkbox} />
                  </div>
                  <span className={styles.container__filters__option__checkbox_text}>
                    {`${switches[key as keyof typeof switches]} (${value})`}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </button>
  );
};

export default WorkspaceFilter;
